import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { startOfMonth, subMonths } from 'date-fns';
import { AnyAction } from 'redux';

import {
  AppState,
  reportFilterStartAccountingDateSelector,
  reportFilterEndAccountingDateSelector,
  changeFilterStartAccountingDate,
  changeFilterEndAccountingDate,
  filterParametersDataSelector,
} from '../../../state';
import { constants, isEqualNullableDate } from '../../../helpers';
import DateRangeFlexSales, {
  DateRangeFlexSalesProps,
  DateRangeFlexSalesValue,
} from '../../DateRangeFlexSales';

type DateSelectorValue = {
  raw: object | null | undefined;
  accept: Date | null | undefined;
};

type AccountingDateRangeFilterProps = Pick<DateRangeFlexSalesProps<Date>, 'spacing'> & {
  maxDaysBetween?: number; // Nueva prop opcional
  requiredIncomplete?: boolean;
  maxDate?: Date;
  startDateSelector?: (state: AppState) => DateSelectorValue;
  endDateSelector?: (state: AppState) => DateSelectorValue;
  changeStartDateAction?: (value: Date | null) => AnyAction;
  changeEndDateAction?: (value: Date | null) => AnyAction;
};

const AccountingDateRangeFilter = ({
  maxDate,
  startDateSelector = reportFilterStartAccountingDateSelector,
  endDateSelector = reportFilterEndAccountingDateSelector,
  changeStartDateAction = changeFilterStartAccountingDate,
  changeEndDateAction = changeFilterEndAccountingDate,
  ...props
}: AccountingDateRangeFilterProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const startDate = useSelector(startDateSelector);
  const endDate = useSelector(endDateSelector);
  const filterParameters = useSelector(filterParametersDataSelector);
  const minDate = startOfMonth(subMonths(new Date(), filterParameters?.filtersLimitMonths || 3));

  const dateValue: DateRangeFlexSalesValue<Date> = useMemo(
    () => [startDate.raw, endDate.raw] as DateRangeFlexSalesValue<Date>,
    [startDate.raw, endDate.raw]
  );

  const handleChange = useCallback(
    ([newStartAccountingDate, newEndAccountingDate]: DateRangeFlexSalesValue<Date>) => {
      if (!isEqualNullableDate(newStartAccountingDate, startDate.raw as Date)) {
        dispatch(changeStartDateAction(newStartAccountingDate));
      } else if (!isEqualNullableDate(newEndAccountingDate, endDate.raw as Date))
        dispatch(changeEndDateAction(newEndAccountingDate));
    },
    [dispatch, endDate, startDate, changeStartDateAction, changeEndDateAction]
  );

  return (
    <DateRangeFlexSales
      {...props}
      maxDate={maxDate || constants.reportFilterRangeMaxDate}
      minDate={minDate}
      startText={t('toolbar.labels.fromDateAccounting')}
      endText={t('toolbar.labels.toDateAccounting')}
      value={dateValue}
      onChange={handleChange}
    />
  );
};

export default AccountingDateRangeFilter;
